import React, { useEffect, useState, useCallback } from 'react';
import { ChefHat, Recycle, Clock, Tag, Snowflake, Leaf, DollarSign, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FOCUSES = [
  { id: 'general', label: 'Everyday' },
  { id: 'glut', label: 'Glut & oversized' },
  { id: 'preserve', label: 'Preserve it' },
];

export function ZeroWasteRecipes({ cropName }) {
  const [focus, setFocus] = useState('general');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    axios.get(`${API}/recipes/${encodeURIComponent(cropName.toLowerCase())}?focus=${focus}`, { withCredentials: true, timeout: 90000 })
      .then(r => { if (live) setData(r.data); })
      .catch(() => { if (live) setError(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [cropName, focus]);

  return (
    <div className="bg-[#FAF6EC] rounded-3xl p-6 border border-[#1A4331]/10" data-testid="zero-waste-recipes-section">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h2 className="text-2xl font-semibold text-[#1A4331] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <ChefHat className="w-6 h-6 text-[#C66B4A]" /> Zero-Waste Kitchen
        </h2>
        <div className="flex gap-2">
          {FOCUSES.map(f => (
            <button key={f.id} onClick={() => setFocus(f.id)} data-testid={`recipe-focus-${f.id}`}
              className={`text-xs font-semibold px-3 py-2 rounded-full transition-colors ${focus === f.id ? 'bg-[#1A4331] text-[#FAF6EC]' : 'bg-white text-[#1A4331] hover:bg-[#E5EBDD]'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-[#7A6B57] mb-4">Use the whole plant, waste nothing — wholefood ideas for your {cropName.toLowerCase()}.</p>

      {loading ? (
        <div className="py-10 text-center" data-testid="recipes-loading">
          <div className="w-10 h-10 border-4 border-[#C66B4A]/30 border-t-[#C66B4A] rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-[#7A6B57] italic">Cooking up zero-waste ideas…</p>
        </div>
      ) : error ? (
        <p className="text-sm text-[#C66B4A] py-4">Couldn&apos;t load recipes right now — try again shortly.</p>
      ) : data && (
        <div className="space-y-4">
          {data.intro && <p className="text-sm text-[#2A3528] italic">{data.intro}</p>}

          <div className="grid md:grid-cols-2 gap-3">
            {data.recipes.map((r, i) => (
              <div key={i} className="bg-white/70 rounded-2xl p-4" data-testid={`recipe-card-${i}`}>
                <h3 className="font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{r.title}</h3>
                <div className="flex flex-wrap gap-2 mt-1.5 mb-2">
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#7A6B57] bg-[#E5EBDD] px-2 py-0.5 rounded-full">
                    <Clock className="w-3 h-3" /> {r.time_minutes} min
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#7A6B57] bg-[#E5EBDD] px-2 py-0.5 rounded-full capitalize">
                    <Tag className="w-3 h-3" /> {r.difficulty}
                  </span>
                  {(r.uses_parts || []).map(p => (
                    <span key={p} className="text-[10px] font-semibold text-[#1A4331] bg-[#6B9F4A]/15 px-2 py-0.5 rounded-full capitalize">{p}</span>
                  ))}
                </div>
                <p className="text-sm text-[#2A3528] leading-relaxed">{r.description}</p>
                {r.zero_waste_angle && (
                  <p className="text-xs text-[#6B9F4A] font-medium mt-2 flex items-start gap-1">
                    <Recycle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {r.zero_waste_angle}
                  </p>
                )}
              </div>
            ))}
          </div>

          {data.use_it_all?.length > 0 && (
            <div className="bg-[#6B9F4A]/8 rounded-2xl p-4 border border-[#6B9F4A]/20">
              <p className="text-xs font-bold tracking-wider uppercase text-[#1A4331] mb-2 flex items-center gap-1">
                <Recycle className="w-4 h-4 text-[#6B9F4A]" /> Use it all
              </p>
              <ul className="space-y-1.5">
                {data.use_it_all.map((u, i) => (
                  <li key={i} className="text-sm text-[#2A3528]">
                    <span className="font-semibold capitalize text-[#1A4331]">{u.part}:</span> {u.idea}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3 text-xs text-[#7A6B57]">
            {data.storage_tip && (
              <p className="flex items-start gap-2"><Snowflake className="w-4 h-4 text-[#8FB4D6] flex-shrink-0" /> {data.storage_tip}</p>
            )}
            {data.compost_note && (
              <p className="flex items-start gap-2"><Leaf className="w-4 h-4 text-[#6B9F4A] flex-shrink-0" /> {data.compost_note}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const RETAILERS = ['New World', "Pak'nSave", 'Woolworths', 'Coles', 'Aldi', 'Farmers Market', 'Local greengrocer', 'Other'];

export function PriceCheckWidget({ cropName }) {
  const [prices, setPrices] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ price: '', retailer: 'New World' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const cropKey = cropName.toLowerCase();

  const load = useCallback(() => {
    axios.get(`${API}/value/crop-prices/${encodeURIComponent(cropKey)}`, { withCredentials: true })
      .then(r => setPrices(r.data))
      .catch(() => {});
  }, [cropKey]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/value/contribute-price`, {
        crop_name: cropKey,
        price: parseFloat(form.price),
        unit: 'kg',
        retailer: form.retailer,
      }, { withCredentials: true });
      setSuccess(`Thanks! +${r.data.xp_awarded} XP — ${r.data.crop_contributions_30d} price${r.data.crop_contributions_30d === 1 ? '' : 's'} shared this month`);
      setShowForm(false);
      setForm({ price: '', retailer: 'New World' });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't share that price — check it and try again.");
    }
    finally { setSubmitting(false); }
  };

  const official = prices?.official_latest;

  return (
    <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm" data-testid="price-check-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-[#1A4331] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <DollarSign className="w-6 h-6 text-[#C9A85B]" /> Store price
          </h2>
          {prices?.blended_price_low != null ? (
            <p className="text-sm text-[#2A3528] mt-2">
              Around <span className="font-bold text-[#1A4331]">${prices.blended_price_low}–${prices.blended_price_high}/kg</span> in stores right now
              {official && <span className="text-xs text-[#7A6B57]"> · official NZ food price data{prices.user_contributions_count > 0 ? ` + ${prices.user_contributions_count} community sighting${prices.user_contributions_count === 1 ? '' : 's'}` : ''}</span>}
            </p>
          ) : (
            <p className="text-sm text-[#7A6B57] mt-2">No recent price data for this crop yet — be the first to share one.</p>
          )}
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="saw-in-store-button"
          className="text-sm font-semibold text-[#1A4331] border-2 border-[#1A4331] py-2 px-4 rounded-full hover:bg-[#1A4331]/5">
          {showForm ? 'Cancel' : 'Saw this in store? +5 XP'}
        </button>
      </div>

      {success && (
        <p className="mt-3 text-sm text-[#6B9F4A] font-medium flex items-center gap-1.5" data-testid="price-contribution-success">
          <Check className="w-4 h-4" /> {success}
        </p>
      )}

      {error && (
        <p className="mt-3 text-sm text-[#C66B4A] font-medium" data-testid="price-contribution-error">{error}</p>
      )}

      {showForm && (
        <form onSubmit={submit} className="mt-4 bg-[#F0F2EB] rounded-2xl p-4 flex flex-wrap items-end gap-3" data-testid="price-contribution-form">
          <div className="flex-1 min-w-[130px]">
            <label className="block text-xs font-semibold text-[#2D241E] mb-1.5">Price per kg ($)</label>
            <input type="number" step="0.01" min="0.1" required value={form.price} data-testid="price-input"
              onChange={e => setForm({ ...form, price: e.target.value })} placeholder="e.g. 8.99"
              className="w-full px-4 py-2.5 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]" />
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-semibold text-[#2D241E] mb-1.5">Where</label>
            <select value={form.retailer} onChange={e => setForm({ ...form, retailer: e.target.value })} data-testid="retailer-select"
              className="w-full px-4 py-2.5 bg-white rounded-xl border border-[#1A4331]/10 outline-none focus:border-[#1A4331]">
              {RETAILERS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button type="submit" disabled={submitting} data-testid="submit-price-button"
            className="bg-[#1A4331] hover:bg-[#255C44] text-[#FAF6EC] font-semibold py-2.5 px-6 rounded-full text-sm disabled:opacity-50">
            {submitting ? 'Sharing…' : 'Share price'}
          </button>
        </form>
      )}
    </div>
  );
}
